import {
    clamp01,
    interpolate,
    tokenState
} from "./animation-keys.js";

// Mirrors the execution-fall keyframes: [t, x%, y%, rotation deg].
// Percentages are relative to the token size.
const FALL_KEYS = [
    [0.00, 0, 0, 0],
    [0.04, 0, 0, 0],
    [0.08, -3, 0, 0],
    [0.13, 3, 0, 0],
    [0.18, -3, 0, 0],
    [0.23, 3, 0, 0],
    [0.28, -3, 0, 0],
    [0.33, 3, 0, 0],
    [0.38, -3, 0, 0],
    [0.42, 0, 0, 0],
    [0.48, 0, 13, 0],
    [0.58, 0, 9, 6],
    [0.70, 0, 9, -5],
    [0.82, 0, 3, 2],
    [1.00, 0, 0, 0]
];

// Mirrors the execution-darken keyframes: [t, grayscale, brightness, red glow].
const DARKEN_KEYS = [
    [0.00, 0, 1, 0],
    [0.42, 0, 1, 0],
    [0.50, 0.3, 1.3, 1],
    [1.00, 1, 0.8, 0]
];

/**
 * Returns the transform/filter the executed token should be drawn with at
 * normalised time `t` (0-1), matching the CSS keyframes.
 *
 * @param  {Number} t
 * @return {Object}
 *         {x, y} offsets as fractions of the token size, `rotate` in degrees,
 *         `filter` as a canvas filter string.
 */
export function tokenStateAt(t) {
    return tokenState(interpolate(FALL_KEYS, t), interpolate(DARKEN_KEYS, t));
}

// Blade drop: 0.18s window starting at t = 0.55/1.3, with a small overshoot.
const bladeYAt = (t) => {

    const start = 0.55 / 1.3;
    const progress = clamp01((t - start) / (0.18 / 1.3));

    if (progress >= 1) {
        return 24;
    }

    return progress < 0.8
        ? 26 * (progress / 0.8) ** 2
        : 26 - 2 * ((progress - 0.8) / 0.2);

};

// Head tumble into the basket: 0.4s window starting at t = 0.62/1.3.
const headAt = (t) => {

    const progress = clamp01((t - 0.62 / 1.3) / (0.4 / 1.3));
    const eased = progress * progress;

    return {
        x: -21 * eased,
        y: 31 * eased,
        rotate: -70 * eased
    };

};

// The whole scene dissolves between t = 0.95/1.3 and the end.
const opacityAt = (t) => {
    const start = 0.95 / 1.3;
    return 0.9 * (1 - clamp01((t - start) / (1 - start)));
};

/**
 * Draws the guillotine scene at normalised animation time `t`, matching the
 * inline SVG in the character template. Coordinates are in the same 0-100
 * space as the SVG viewBox.
 *
 * @param {CanvasRenderingContext2D} context
 * @param {Number} x
 *        Left edge of the token area in canvas pixels.
 * @param {Number} y
 *        Top edge of the token area in canvas pixels.
 * @param {Number} size
 *        Token size in canvas pixels.
 * @param {Number} t
 *        Normalised animation time, 0-1.
 */
export function drawGuillotine(context, x, y, size, t) {

    context.save();
    context.translate(x, y);
    context.scale(size / 100, size / 100);
    context.globalAlpha = opacityAt(t);

    // Frame.
    context.fillStyle = "#6b4a2f";
    context.fillRect(12, 4, 76, 8);
    context.fillStyle = "#5a3d24";
    context.fillRect(18, 10, 7, 82);
    context.fillRect(75, 10, 7, 82);
    context.fillStyle = "#4a341f";
    context.fillRect(10, 90, 80, 7);
    context.fillStyle = "#8a6a45";
    context.fillRect(49, 12, 2, 6);

    // Kneeling victim.
    context.fillStyle = "#4a5a6a";
    context.beginPath();
    context.moveTo(40, 64);
    context.bezierCurveTo(33, 71, 31, 79, 31, 90);
    context.lineTo(69, 90);
    context.bezierCurveTo(69, 79, 66, 70, 60, 64);
    context.closePath();
    context.fill();

    // Stocks.
    context.fillStyle = "#6b4a2f";
    context.fillRect(28, 58, 44, 7);

    // Blade.
    context.save();
    context.translate(0, bladeYAt(t));
    context.fillStyle = "#a8b0ba";
    context.beginPath();
    context.moveTo(32, 18);
    context.lineTo(68, 18);
    context.lineTo(68, 36);
    context.lineTo(50, 46);
    context.lineTo(32, 36);
    context.closePath();
    context.fill();
    context.fillStyle = "#6e7883";
    context.beginPath();
    context.moveTo(32, 36);
    context.lineTo(50, 46);
    context.lineTo(68, 36);
    context.lineTo(68, 33);
    context.lineTo(50, 43);
    context.lineTo(32, 33);
    context.closePath();
    context.fill();
    context.restore();

    // Head.
    const head = headAt(t);
    context.save();
    context.translate(50 + head.x, 50 + head.y);
    context.rotate(head.rotate * Math.PI / 180);
    context.fillStyle = "#c9a17a";
    context.beginPath();
    context.arc(0, 0, 8, 0, Math.PI * 2);
    context.fill();
    context.restore();

    // Basket and the blood inside it.
    context.fillStyle = "#4a341f";
    context.beginPath();
    context.moveTo(18, 90);
    context.lineTo(40, 90);
    context.lineTo(35, 76);
    context.lineTo(23, 76);
    context.closePath();
    context.fill();
    context.fillStyle = "#7a1414";
    context.beginPath();
    context.ellipse(29, 86, 5.5, 2.5, 0, 0, Math.PI * 2);
    context.fill();

    context.restore();

}
